import { useState, useMemo, useEffect } from "react";
import { formatLocalYmd } from "@/utils/dateTime";
import "@/pages/Dashboard.css";
import { Group } from "@/constants/Groups";
import { LdapStatus } from "@/constants/LdapStatus";
import DateRangePicker from "@/components/common/DateRangePicker";
import Table from "@/components/common/Table";
import { getSummary, getLdapsAndDisplayNames } from "@/api/dashboardApi";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";

/**
 * @typedef {Object} SummaryData
 * @property {number} totalMembers - The total number of members.
 * @property {number} totalCompletedTickets - The total count of completed Jira tickets.
 * @property {number} totalMergedCls - The total count of merged CLs (Gerrit Change Lists).
 * @property {number} totalMergedLoc - The total count of merged LOCs (Lines of Code).
 * @property {number} totalMeetingHours - The total hours of Google meetings attended.
 * @property {number} totalMessagesSent - The total count of chat messages sent (Google Chat and Teams Chat).
 */

/**
 * @typedef {Object} TableMemberData
 * @property {string} ldap - The LDAP of the member.
 * @property {number} jiraTicketsDone - Number of Jira tickets completed.
 * @property {number} mergedCls - Number of CLs merged.
 * @property {number} mergedLoc - Number of LOCs merged.
 * @property {number} meetingHours - Total duration (in hours) of meetings attended.
 * @property {number} chatMessagesSent - Number of chat messages sent.
 */

/**
 * Dashboard Component
 *
 * Provides a comprehensive overview of team activities and key metrics,
 * offering interactive filtering options. It fetches and displays summary data
 * and a detailed table of member-specific metrics.
 *
 * Features:
 * - Filter data by a selectable date range.
 * - Filter by team groups (Interns, Employees, Volunteers, etc.).
 * - Optionally include or exclude terminated members.
 * - Trigger data fetch with a search button.
 *
 * State:
 * - `selectedStartDate` (string): The start date of the range filter in `YYYY-MM-DD` format.
 * - `selectedEndDate` (string): The end date of the range filter in `YYYY-MM-DD` format.
 * - `groups` (Group[]): Currently selected team groups for filtering.
 * - `includeTerminated` (boolean): Whether terminated members are included in results.
 * - `summaryData` (SummaryData): The fetched summary data from the API.
 * - `tableData` (TableMemberData[]): The detailed member data for the table.
 *
 * Default Behavior:
 * - Date range defaults from the first day of the current month to today (in browser local time).
 * - Initial group selection defaults to `[Group.Interns]`.
 * - Initial `includeTerminated` state is set to `false`.
 * - `summaryData` is initialized with all-zero values.
 * - `tableData` is initialized as an empty array `[]`.
 *
 * Component Props:
 * - None.
 *
 * Handlers:
 * - `handleGroupChange(groupName: Group)`: Toggles selection of a group.
 * - `handleDateChange({ startDate, endDate }: { startDate: string; endDate: string })`: Updates selected date range.
 * - `handleSearch()`: Fetches summary data using current filters and updates state.
 *
 * Notes:
 * - The `getSummary` API is asynchronous and updates `summaryData` and `tableData` on success.
 * - Date values are managed in browser local time to avoid UTC/local date boundary inconsistencies.
 *
 *
 * @component
 * @returns {JSX.Element} The rendered Dashboard page.
 */
const Dashboard = () => {
  const today = new Date();
  const firstDayOfThisMonth = new Date(
    today.getFullYear(),
    today.getMonth(),
    1,
  );

  const defaultStart = formatLocalYmd(firstDayOfThisMonth);
  const defaultEnd = formatLocalYmd(today);

  const [selectedStartDate, setSelectedStartDate] = useState(defaultStart);
  const [selectedEndDate, setSelectedEndDate] = useState(defaultEnd);

  const [includeTerminated, setIncludeTerminated] = useState(false);

  const [groups, setGroups] = useState([Group.Interns, Group.Employees]);
  const groupsData = Object.values(Group);

  const [error, setError] = useState("");

  const [summaryData, setSummaryData] = useState({
    totalMembers: 0,
    totalCompletedTickets: 0,
    totalMergedCls: 0,
    totalMergedLoc: 0,
    totalMeetingHours: 0,
    totalMessagesSent: 0,
  });

  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [tableData, setTableData] = useState([]);

  /**
   * Columns configuration for the members table.
   * Must align with {@link TableMemberData}.
   */
  const tableColumns = [
    { header: "LDAP", accessor: "ldap", sortable: true },
    {
      header: "Jira Tickets (Done)",
      accessor: "jiraTicketsDone",
      sortable: true,
    },
    { header: "Merged CLs", accessor: "mergedCls", sortable: true },
    { header: "Merged LOC", accessor: "mergedLoc", sortable: true },
    { header: "Meeting Hours", accessor: "meetingHours", sortable: true },
    {
      header: "Chat Messages Sent",
      accessor: "chatMessagesSent",
      sortable: true,
    },
  ];

  /**
   * Toggles the selection of a team group.
   * This function does not return a value; instead, it updates component state.
   * @param {Group} groupName - The group to add or remove from the selection.
   */
  const handleGroupChange = (groupName) => {
    setGroups((prevGroups) => {
      const updatedGroups = prevGroups.includes(groupName)
        ? prevGroups.filter((g) => g !== groupName)
        : [...prevGroups, groupName];

      if (updatedGroups.length > 0) {
        setError("");
      }

      return updatedGroups;
    });
  };

  /**
   * Updates the state with the new selected date range.
   * @param {{ startDate: string; endDate: string }} newDates
   */
  const handleDateChange = ({ startDate, endDate }) => {
    setSelectedStartDate(startDate);
    setSelectedEndDate(endDate);
  };

  /**
   * Fetches and processes data from the API based on the provided filters.
   * Updates `summaryData` and `tableData` states.
   * @param {string} startDate - The start date for the data fetch.
   * @param {string} endDate - The end date for the data fetch.
   * @param {Group[]} groups - The list of groups to filter by.
   * @param {boolean} includeTerminated - Whether to include terminated members.
   */
  const fetchDataAndRender = async ({
    startDate,
    endDate,
    groups,
    includeTerminated,
  }) => {
    try {
      const [{ data: summaryResult }, { data: ldapsResult }] =
        await Promise.all([
          getSummary({
            startDate: startDate,
            endDate: endDate,
            groups: groups,
            includeTerminated: includeTerminated,
          }),
          getLdapsAndDisplayNames({
            status: includeTerminated ? LdapStatus.All : LdapStatus.Active,
            groups: groups,
          }),
        ]);

      let totalLdaps = 0;
      for (const group in ldapsResult) {
        const statusObject = ldapsResult[group];
        for (const status in statusObject) {
          const ldapMap = statusObject[status];
          totalLdaps += Object.keys(ldapMap).length;
        }
      }

      const accumulatedSummary = summaryResult.reduce(
        (acc, member) => {
          acc.totalCompletedTickets += member.jira_issue_done;
          acc.totalMergedCls += member.cl_merged;
          acc.totalMergedLoc += member.loc_merged;
          acc._totalMeetingHoursInCents += Math.round(
            member.meeting_hours * 100,
          );
          acc.totalMessagesSent += member.chat_count;
          return acc;
        },
        {
          totalCompletedTickets: 0,
          totalMergedCls: 0,
          totalMergedLoc: 0,
          _totalMeetingHoursInCents: 0,
          totalMessagesSent: 0,
        },
      );

      const finalTotalMeetingHours =
        accumulatedSummary._totalMeetingHoursInCents / 100;

      const newTableData = summaryResult.map((member) => ({
        ldap: member.ldap,
        jiraTicketsDone: member.jira_issue_done,
        mergedCls: member.cl_merged,
        mergedLoc: member.loc_merged,
        meetingHours: member.meeting_hours,
        chatMessagesSent: member.chat_count,
      }));

      setTableData(newTableData);

      setSummaryData({
        totalCompletedTickets: accumulatedSummary.totalCompletedTickets,
        totalMergedCls: accumulatedSummary.totalMergedCls,
        totalMergedLoc: accumulatedSummary.totalMergedLoc,
        totalMessagesSent: accumulatedSummary.totalMessagesSent,
        totalMeetingHours: finalTotalMeetingHours,
        totalMembers: totalLdaps,
      });
    } catch (err) {
      console.log(err);
    } finally {
      console.log(summaryData);
    }
  };

  /**
   * Handler for the initial data fetch when the component mounts.
   */
  const handleInitialLoad = () => {
    fetchDataAndRender({
      startDate: defaultStart,
      endDate: defaultEnd,
      groups: [Group.Interns, Group.Employees],
      includeTerminated: false,
    });
  };

  /**
   * Handler for the search button click.
   */
  const handleSearchClick = () => {
    if (groups.length === 0) {
      setError("Groups are required.");
    } else {
      setError("");
      fetchDataAndRender({
        startDate: selectedStartDate,
        endDate: selectedEndDate,
        groups: groups,
        includeTerminated: includeTerminated,
      });
    }
  };

  /**
   * Toggles the sorting direction or sets a new column to sort by.
   * @param {string} columnAccessor - The key of the column to sort by.
   */
  const handleSort = (columnAccessor) => {
    if (sortColumn === columnAccessor) {
      setSortDirection((prevDir) => (prevDir === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(columnAccessor);
      setSortDirection("asc");
    }
  };

  /**
   * Memoized value for the sorted table data. Recalculates only when
   * `tableData`, `sortColumn`, or `sortDirection` changes.
   * @type {TableMemberData[]}
   */
  const sortedTableData = useMemo(() => {
    if (!sortColumn || tableData.length === 0) {
      return tableData;
    }
    const sortableData = [...tableData];
    sortableData.sort((a, b) => {
      const valA = a[sortColumn];
      const valB = b[sortColumn];
      let comparison = 0;
      if (sortColumn === "ldap") {
        comparison = String(valA).localeCompare(String(valB));
      } else {
        comparison = Number(valA) - Number(valB);
      }
      return sortDirection === "asc" ? comparison : -comparison;
    });
    return sortableData;
  }, [tableData, sortColumn, sortDirection]);

  /**
   * Effect hook to trigger an initial data fetch and set the user's name from the Cloudflare JWT cookie.
   *
   * This hook runs once on component mount. It first triggers the `handleInitialLoad` function to perform
   * the initial data fetch. Then, it checks if a Cloudflare JWT Authorization cookie (`CF_Authorization`)
   * is present. If found, it extracts the user's name and sets the greeting message accordingly.
   *
   * @hook
   * @function useEffect
   * @see getCookie
   * @see extractCloudflareUserName
   */

  useEffect(() => {
    handleInitialLoad();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="dashboard-page">
      <div className="flex flex-row gap-3 overflow-x-auto pb-2">
        {[
          { title: "Members", value: summaryData.totalMembers },
          {
            title: "Jira Tickets (Done)",
            value: summaryData.totalCompletedTickets,
          },
          { title: "Merged CLs", value: summaryData.totalMergedCls },
          { title: "Merged LOC", value: summaryData.totalMergedLoc },
          { title: "Meeting Hours", value: summaryData.totalMeetingHours },
          { title: "Chat Messages Sent", value: summaryData.totalMessagesSent },
        ].map((item, index) => (
          <Card
            key={index}
            className="flex-1 min-w-[140px] gap-0 py-3"
            data-testid={`card-${item.title.replace(/\s+/g, "-")}`}
          >
            <CardHeader className="p-3 pb-0">
              <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {item.title}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3 pt-1">
              <div className="text-lg font-bold">{item.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="search-filter-row">
        <DateRangePicker
          defaultStartDate={defaultStart}
          defaultEndDate={defaultEnd}
          onChange={handleDateChange}
        />

        <div className="checkbox-container">
          {groupsData.map((group) => {
            const isRequired = [
              Group.Interns,
              Group.Employees,
              Group.Volunteers,
            ].includes(group);
            const errorClass = error ? "error" : "";

            return (
              <div key={group} className="checkbox-item">
                <label
                  className={`group-label ${errorClass} ${isRequired ? "required" : ""}`}
                >
                  <input
                    type="checkbox"
                    checked={groups.includes(group)}
                    onChange={() => handleGroupChange(group)}
                  />
                  <span>{group}</span>
                </label>
              </div>
            );
          })}

          <label className="checkbox-item">
            <input
              type="checkbox"
              checked={includeTerminated}
              onChange={(e) => setIncludeTerminated(e.target.checked)}
            />
            <span>Include Terminated Members</span>
          </label>
        </div>

        {error && <div className="error-message">{error}</div>}

        <Button size="sm" onClick={handleSearchClick}>
          Search
        </Button>
      </div>

      <Table
        className="table-container"
        columns={tableColumns}
        data={sortedTableData}
        onSort={handleSort}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
      />
    </div>
  );
};

export default Dashboard;
